// SPDX-License-Identifier: MIT
// Streaming agent panel (desktop) — a NARRATED ACTIVITY FEED, the SOTA pattern
// (June 2026) for showing an autonomous agent at work, replacing the old frozen
// "Genesis builds…" + final text dump. It inhabits the existing carnet aesthetic
// (2px ink borders, hard offset shadows, paper-warm, orange/sage) — it does NOT
// invent a new look. The caller feeds it PARSED events (parseClaudeStreamLine) so
// this component is pure DOM + unit-testable without Tauri.
//
// Layout (research-grounded): phase chips → scrollable feed of friendly step rows
// + streaming prose → pinned status bar (current activity · elapsed · Stop) →
// compose row (follow-up task + terminal fallback) → collapsed "journal technique"
// drawer (raw NDJSON, off by default). No fake % bar (duration unknown). Motion is
// gated on prefers-reduced-motion in CSS; completed steps announce via aria-live.
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import { parseClaudeStreamLine, type AgentStreamEvent } from '../platform/claude-stream';

export interface AgentPanelCallbacks {
  onRun: (prompt: string) => void;     // user submitted a (follow-up) task
  onCancel: () => void;                // user pressed Stop
  onOpenTerminal: () => void;          // fallback: open a real terminal
  onNewSession?: () => void;           // user asked to start a fresh session (no --continue)
}

export interface AgentPanelHandle {
  el: HTMLElement;
  start(): void;                       // enter running state (timer + status), wiping the feed
  beginRequest(prompt: string, fresh: boolean): void; // begin a new request: clear (fresh) or append a separator
  hydrate(lines: string[]): Promise<void>; // replay stored NDJSON lines without entering running state (chunked async)
  push(ev: AgentStreamEvent): void;    // a parsed stream event
  rawLine(line: string): void;         // append to the technical journal
  fail(message: string): void;         // hard transport/spawn error
  setElapsed(totalSeconds: number): void;
  destroy(): void;
}

function fmtElapsed(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// Read-only, static replay of a parsed event stream into the SAME row markup the
// live feed builds (.agent-step / .agent-prose / .agent-outcome). Extracted so the
// session-history accordion (Level 3 detail) can reuse it without duplicating the
// row construction. No timer, no status bar, no streaming state — just the rows.
// Consecutive tool_start events close the prior step as "done" (mirrors push()).
export function renderStaticFeed(events: AgentStreamEvent[]): HTMLElement {
  const feed = document.createElement('div');
  feed.className = 'agent-panel__feed agent-panel__feed--static';
  let runningStep: HTMLElement | null = null;
  let proseEl: HTMLElement | null = null;

  const closeStep = (mark: 'done' | 'error', target?: string): void => {
    if (!runningStep) return;
    runningStep.classList.remove('is-running');
    runningStep.classList.add(mark === 'done' ? 'is-done' : 'is-error');
    if (target) {
      const tgt = runningStep.querySelector('.agent-step__target');
      if (tgt && !tgt.textContent) tgt.textContent = target;
    }
    runningStep = null;
  };

  for (const ev of events) {
    switch (ev.kind) {
      case 'tool_start': {
        closeStep('done');
        proseEl = null;
        const rowEl = document.createElement('div');
        rowEl.className = 'agent-step is-running';
        rowEl.innerHTML = `<span class="agent-step__icon" aria-hidden="true"></span><span class="agent-step__text"></span><span class="agent-step__target"></span>`;
        rowEl.querySelector('.agent-step__text')!.textContent = ev.label;
        if (ev.target) rowEl.querySelector('.agent-step__target')!.textContent = ev.target;
        feed.appendChild(rowEl);
        runningStep = rowEl;
        break;
      }
      case 'tool_done':
        closeStep('done', ev.target);
        break;
      case 'text': {
        if (!proseEl) {
          proseEl = document.createElement('p');
          proseEl.className = 'agent-prose';
          feed.appendChild(proseEl);
        }
        proseEl.textContent = (proseEl.textContent ?? '') + ev.delta;
        break;
      }
      case 'done': {
        closeStep(ev.isError ? 'error' : 'done');
        const card = document.createElement('div');
        card.className = ev.isError ? 'agent-outcome agent-outcome--err' : 'agent-outcome agent-outcome--ok';
        card.setAttribute('role', 'status');
        const head = ev.isError ? t('delivery.agentErrHead') : t('delivery.agentDoneHead');
        const meta: string[] = [];
        if (typeof ev.durationMs === 'number') meta.push(fmtElapsed(Math.round(ev.durationMs / 1000)));
        if (typeof ev.costUsd === 'number' && ev.costUsd > 0) meta.push(`$${ev.costUsd.toFixed(3)}`);
        card.innerHTML = `
          <div class="agent-outcome__head">${escapeHtml(head)}</div>
          <div class="agent-outcome__body"></div>
          ${meta.length ? `<div class="agent-outcome__meta">${escapeHtml(meta.join(' · '))}</div>` : ''}`;
        card.querySelector('.agent-outcome__body')!.textContent = ev.text || (ev.isError ? t('delivery.agentError') : '');
        feed.appendChild(card);
        proseEl = null;
        break;
      }
      // 'init' / 'thinking' only drive live status/phase chrome → no static row.
      default:
        break;
    }
  }
  // Any step still "running" at the end of a stored stream simply settles to done.
  closeStep('done');
  return feed;
}

export function renderAgentPanel(cb: AgentPanelCallbacks): AgentPanelHandle {
  const panel = document.createElement('div');
  panel.className = 'agent-panel';
  panel.setAttribute('data-component', 'agent-panel');
  panel.innerHTML = `
    <div class="agent-panel__head">
      <div class="agent-panel__title">${escapeHtml(t('delivery.agentTitle'))}</div>
      <button type="button" class="agent-panel__new" data-component="agent-new" title="${escapeHtml(t('delivery.agentNewSessionTitle'))}">${escapeHtml(t('delivery.agentNewSession'))}</button>
      <div class="agent-panel__phases" data-component="agent-phases" aria-hidden="true">
        <span class="agent-phase" data-phase="1">${escapeHtml(t('delivery.agentPhase1'))}</span>
        <span class="agent-phase" data-phase="2">${escapeHtml(t('delivery.agentPhase2'))}</span>
        <span class="agent-phase" data-phase="3">${escapeHtml(t('delivery.agentPhase3'))}</span>
      </div>
    </div>
    <div class="agent-panel__feed" data-component="agent-feed" role="log" aria-live="polite" aria-relevant="additions"></div>
    <div class="agent-panel__status" data-component="agent-status" hidden>
      <span class="agent-panel__status-dot" aria-hidden="true"></span>
      <span class="agent-panel__status-text" data-component="agent-status-text"></span>
      <span class="agent-panel__elapsed" data-component="agent-elapsed">0:00</span>
      <button type="button" class="agent-panel__stop" data-component="agent-stop">${escapeHtml(t('delivery.agentStop'))}</button>
    </div>
    <div class="agent-panel__compose">
      <textarea class="agent-panel__input" data-component="agent-input" rows="2" placeholder="${escapeHtml(t('delivery.agentPlaceholder'))}"></textarea>
      <div class="agent-panel__row">
        <button class="agent-panel__run" data-component="agent-run" type="button">${escapeHtml(t('delivery.agentBtn'))}</button>
        <button class="agent-panel__term" data-component="agent-term" type="button">${escapeHtml(t('delivery.termFallback'))}</button>
      </div>
    </div>
    <details class="agent-panel__log">
      <summary>${escapeHtml(t('delivery.agentLog'))}</summary>
      <pre class="agent-panel__log-out" data-component="agent-log"></pre>
    </details>`;

  const feed = panel.querySelector<HTMLElement>('[data-component="agent-feed"]')!;
  const phases = panel.querySelector<HTMLElement>('[data-component="agent-phases"]')!;
  const statusBar = panel.querySelector<HTMLElement>('[data-component="agent-status"]')!;
  const statusText = panel.querySelector<HTMLElement>('[data-component="agent-status-text"]')!;
  const elapsedEl = panel.querySelector<HTMLElement>('[data-component="agent-elapsed"]')!;
  const stopBtn = panel.querySelector<HTMLButtonElement>('[data-component="agent-stop"]')!;
  const input = panel.querySelector<HTMLTextAreaElement>('[data-component="agent-input"]')!;
  const runBtn = panel.querySelector<HTMLButtonElement>('[data-component="agent-run"]')!;
  const termBtn = panel.querySelector<HTMLButtonElement>('[data-component="agent-term"]')!;
  const logOut = panel.querySelector<HTMLElement>('[data-component="agent-log"]')!;
  const newBtn = panel.querySelector<HTMLButtonElement>('[data-component="agent-new"]')!;

  let runningStep: HTMLElement | null = null; // the in-flight step row
  let proseEl: HTMLElement | null = null;     // current streaming prose block
  let finished = false;

  const setPhase = (n: 1 | 2 | 3) => {
    for (const el of phases.querySelectorAll<HTMLElement>('.agent-phase')) {
      el.classList.toggle('is-active', Number(el.dataset.phase) <= n);
      el.classList.toggle('is-current', Number(el.dataset.phase) === n);
    }
  };
  const setStatus = (text: string) => { statusText.textContent = text; };
  const closeRunningStep = (mark: 'done' | 'error', target?: string) => {
    if (!runningStep) return;
    runningStep.classList.remove('is-running');
    runningStep.classList.add(mark === 'done' ? 'is-done' : 'is-error');
    if (target) {
      const tgt = runningStep.querySelector('.agent-step__target');
      if (tgt && !tgt.textContent) tgt.textContent = target;
    }
    runningStep = null;
  };
  const autoscroll = () => { feed.scrollTop = feed.scrollHeight; };

  const setRunning = (running: boolean) => {
    statusBar.hidden = !running;
    runBtn.disabled = running;
    input.disabled = running;
  };

  // Enter the running state (timer/status/phase) WITHOUT touching the feed — so a
  // follow-up request can keep its prior rows. `start()` wraps it with a wipe for
  // back-compat (a brand-new panel).
  const runningState = () => {
    finished = false;
    runningStep = null;
    proseEl = null;
    setPhase(1);
    setStatus(t('delivery.agentStarting'));
    setRunning(true);
  };

  const start = () => {
    feed.innerHTML = '';
    runningState();
  };

  // Begin a new request in an existing panel. A FRESH session wipes the feed; a
  // follow-up keeps the history and inserts a labelled divider so the user can see
  // where the new request begins.
  const beginRequest = (prompt: string, fresh: boolean) => {
    if (fresh) {
      feed.innerHTML = '';
    } else {
      const sep = document.createElement('div');
      sep.className = 'agent-panel__sep';
      sep.setAttribute('data-component', 'agent-sep');
      sep.textContent = `— ${prompt.slice(0, 80)} —`;
      feed.appendChild(sep);
    }
    runningState();
    autoscroll();
  };

  // Replay stored NDJSON lines into the feed (re-open path) WITHOUT entering the
  // running state — the build is already over; we only reconstruct what happened.
  // Chunked async: processes CHUNK_SIZE lines per tick so the main thread is released
  // between batches — prevents a large history from freezing the UI on project open.
  // We always yield at least once after inserting the placeholder so the loading
  // state paints before any DOM mutations from line processing.
  const HYDRATE_CHUNK = 50;
  const hydrate = async (lines: string[]): Promise<void> => {
    if (lines.length === 0) return;

    // Show a lightweight loading placeholder while replaying, removed on completion.
    const placeholder = document.createElement('p');
    placeholder.className = 'agent-panel__hydrating';
    placeholder.setAttribute('data-component', 'agent-hydrating');
    placeholder.textContent = t('delivery.agentHydrating');
    feed.appendChild(placeholder);

    // Always yield once so the placeholder renders before we begin processing.
    await new Promise<void>((r) => setTimeout(r, 0));

    for (let i = 0; i < lines.length; i += HYDRATE_CHUNK) {
      const chunk = lines.slice(i, i + HYDRATE_CHUNK);
      for (const line of chunk) {
        rawLine(line);
        const ev = parseClaudeStreamLine(line);
        if (ev) push(ev);
      }
      // Yield to the event loop between chunks so the browser can paint.
      if (i + HYDRATE_CHUNK < lines.length) {
        await new Promise<void>((r) => setTimeout(r, 0));
      }
    }

    placeholder.remove();
  };

  const push = (ev: AgentStreamEvent) => {
    switch (ev.kind) {
      case 'init':
        setPhase(1);
        setStatus(t('delivery.agentStarting'));
        break;
      case 'thinking':
        setStatus(t('delivery.agentThinking'));
        break;
      case 'tool_start': {
        setPhase(2);
        closeRunningStep('done'); // a new tool means the previous one finished
        const row = document.createElement('div');
        row.className = 'agent-step is-running';
        row.innerHTML = `<span class="agent-step__icon" aria-hidden="true"></span><span class="agent-step__text"></span><span class="agent-step__target"></span>`;
        row.querySelector('.agent-step__text')!.textContent = ev.label;
        if (ev.target) row.querySelector('.agent-step__target')!.textContent = ev.target;
        feed.appendChild(row);
        runningStep = row;
        setStatus(ev.label);
        autoscroll();
        break;
      }
      case 'tool_done':
        closeRunningStep('done', ev.target);
        break;
      case 'text': {
        if (!proseEl) {
          proseEl = document.createElement('p');
          proseEl.className = 'agent-prose';
          feed.appendChild(proseEl);
        }
        proseEl.textContent = (proseEl.textContent ?? '') + ev.delta;
        autoscroll();
        break;
      }
      case 'done':
        finished = true;
        closeRunningStep(ev.isError ? 'error' : 'done');
        setRunning(false);
        renderOutcome(ev);
        break;
    }
  };

  const renderOutcome = (ev: Extract<AgentStreamEvent, { kind: 'done' }>) => {
    const card = document.createElement('div');
    card.className = ev.isError ? 'agent-outcome agent-outcome--err' : 'agent-outcome agent-outcome--ok';
    card.setAttribute('role', 'status');
    const head = ev.isError ? t('delivery.agentErrHead') : t('delivery.agentDoneHead');
    const meta: string[] = [];
    if (typeof ev.durationMs === 'number') meta.push(fmtElapsed(Math.round(ev.durationMs / 1000)));
    if (typeof ev.costUsd === 'number' && ev.costUsd > 0) meta.push(`$${ev.costUsd.toFixed(3)}`);
    card.innerHTML = `
      <div class="agent-outcome__head">${escapeHtml(head)}</div>
      <div class="agent-outcome__body"></div>
      ${meta.length ? `<div class="agent-outcome__meta">${escapeHtml(meta.join(' · '))}</div>` : ''}`;
    card.querySelector('.agent-outcome__body')!.textContent = ev.text || (ev.isError ? t('delivery.agentError') : '');
    feed.appendChild(card);
    setPhase(3);
    autoscroll();
  };

  const rawLine = (line: string) => {
    // Keep the technical journal bounded (drop the oldest if it grows huge).
    logOut.textContent = (logOut.textContent ?? '') + line + '\n';
    if (logOut.textContent.length > 200_000) logOut.textContent = logOut.textContent.slice(-150_000);
  };

  const fail = (message: string) => {
    if (finished) return;
    finished = true;
    closeRunningStep('error');
    setRunning(false);
    const card = document.createElement('div');
    card.className = 'agent-outcome agent-outcome--err';
    card.setAttribute('role', 'status');
    card.innerHTML = `<div class="agent-outcome__head">${escapeHtml(t('delivery.agentErrHead'))}</div><div class="agent-outcome__body"></div>`;
    card.querySelector('.agent-outcome__body')!.textContent = message;
    feed.appendChild(card);
    autoscroll();
  };

  const setElapsed = (s: number) => { elapsedEl.textContent = fmtElapsed(s); };

  // ── wiring ────────────────────────────────────────────────────────────────
  stopBtn.addEventListener('click', () => { setStatus(t('delivery.agentStopping')); cb.onCancel(); });
  runBtn.addEventListener('click', () => {
    const prompt = input.value.trim();
    if (!prompt) { input.focus(); return; }
    input.value = '';
    cb.onRun(prompt);
  });
  termBtn.addEventListener('click', () => cb.onOpenTerminal());
  newBtn.addEventListener('click', () => cb.onNewSession?.());

  return {
    el: panel,
    start,
    beginRequest,
    hydrate,
    push,
    rawLine,
    fail,
    setElapsed,
    destroy: () => { panel.remove(); },
  };
}
